/**
 * "Entrar como este aluno" — as regras da sessão de suporte.
 *
 * ## O que esta rota é, e por que ela mora numa função pura
 *
 * É a porta mais perigosa que o produto tem: um pedido bem-sucedido devolve uma
 * sessão de aluno **sem senha**. Se ela vazar, qualquer pessoa entra na conta de
 * qualquer aluno da instalação.
 *
 * O resto do painel resolve escopo dentro da consulta — `requireClient` filtra
 * por `agency_id` e o Postgres devolve zero linhas para quem não pode. Isso
 * funciona e continua valendo aqui, mas tem um defeito para esta porta em
 * particular: **a decisão fica dentro de um `where`**, e um `where` não tem como
 * ser testado sem banco. Um refactor que trocasse a junção por outra passaria no
 * typecheck e abriria a porta em silêncio.
 *
 * Aqui a decisão é uma função sobre linhas já lidas: quem pergunta, qual área,
 * qual matrícula. Ela roda **depois** do filtro por agência, e não no lugar dele
 * — as duas travas existem, e é de propósito que a segunda saiba dizer não
 * sozinha.
 *
 * ## Por que o prazo é de minutos
 *
 * Porque a sessão é para **olhar**. Quinze minutos cobrem "abre a vitrine, abre o
 * curso, confere o que o grupo libera, sai"; não cobrem "deixa aberta a tarde
 * inteira numa aba". Uma sessão de suporte esquecida num computador
 * compartilhado é o vazamento que este número fecha, e ela não desliza: o relógio
 * conta a partir de quando foi criada, e usar não estende.
 */

import { alcancaCliente, type EscopoDeClientes } from '../escopo-de-clientes.js';

/** Quinze minutos. Ver o cabeçalho — é para olhar, não para trabalhar. */
export const SUPORTE_TTL_SEGUNDOS = 15 * 60;

/** Quem pede. É o recorte de `SessionData` que a decisão precisa. */
export interface OperadorQuePede {
  agencyId: string;
  role: 'admin' | 'member' | 'client_viewer';
  /**
   * O alcance de clientes da sessão. Era `scopedClientId: string | null`, e a
   * comparação era escrita à mão aqui — a SEGUNDA comparação de escopo do
   * repositório. Agora ela é a mesma função de `lib/scope.ts`, e
   * `escopo-area.ts` já registrou o preço de cópias que divergem.
   */
  escopo: EscopoDeClientes;
}

/** A área alvo, como ela veio do banco — sem filtro nenhum aplicado. */
export interface AreaAlvo {
  id: string;
  agencyId: string;
  clientId: string;
}

/** A matrícula que prova que esta pessoa é aluna DESTA área. */
export interface MatriculaAlvo {
  areaId: string;
  memberId: string;
}

/** A conta do aluno. `disabled` não entra — ver o veredito. */
export interface ContaAlvo {
  id: string;
  status: 'pending' | 'active' | 'disabled';
}

export type VeredictoDeSuporte =
  | { ok: true }
  | { ok: false; status: 403 | 404 | 409; codigo: string; motivo: string };

/**
 * Um operador pode entrar como este aluno?
 *
 * ## A ordem das recusas é a resposta, e não um detalhe
 *
 * "Área não encontrada" vem antes de tudo e é a mesma frase para área
 * inexistente e para área de outra agência. Distingui-las transformaria esta rota
 * num varredor: quem tivesse uma conta de operador em qualquer agência descobriria
 * quais áreas existem na instalação inteira chutando uuids — e cada acerto seria
 * o alvo seguinte.
 *
 * ## Por que `client_viewer` é recusado mesmo no cliente dele
 *
 * O papel existe para o cliente ver os próprios números sem poder mexer em nada
 * (`requireWriteAccess`). Uma sessão de aluno não é leitura: ela é uma
 * credencial, e quem a tem passa a poder abrir vídeo protegido, baixar material e
 * ver o e-mail de quem estuda ali. Dar isso a um papel definido como
 * "não escreve" seria contornar o papel pela porta lateral.
 */
export function podeEntrarComoAluno(
  operador: OperadorQuePede,
  area: AreaAlvo,
  matricula: MatriculaAlvo | null,
  conta: ContaAlvo | null,
): VeredictoDeSuporte {
  if (operador.agencyId !== area.agencyId) {
    return { ok: false, status: 404, codigo: 'not_found', motivo: 'Área não encontrada.' };
  }

  /*
   * O cliente fora do escopo vem ANTES do papel, e vale para os três papéis: o
   * 404 do cliente errado esconde a existência da área. Desde a leva da equipe
   * um `member` também pode ser escopado, então a pergunta deixou de ser sobre
   * quem é a pessoa e passou a ser sobre o que a sessão alcança.
   */
  if (!alcancaCliente(operador.escopo, area.clientId)) {
    return { ok: false, status: 404, codigo: 'not_found', motivo: 'Área não encontrada.' };
  }

  if (operador.role === 'client_viewer') {
    return {
      ok: false,
      status: 403,
      codigo: 'suporte_sem_permissao',
      motivo: 'Quem só consulta não entra como aluno. Peça a quem administra a área.',
    };
  }

  if (!matricula || matricula.areaId !== area.id) {
    return { ok: false, status: 404, codigo: 'not_found', motivo: 'Matrícula não encontrada.' };
  }

  if (!conta || conta.id !== matricula.memberId) {
    return { ok: false, status: 404, codigo: 'not_found', motivo: 'Matrícula não encontrada.' };
  }

  /*
   * Conta desativada não abre — e a recusa é explícita em vez de deixar entrar.
   *
   * A sessão até seria criada, e a primeira tela do aluno responderia 401 ("Este
   * acesso foi desativado"), jogando o operador na tela de login da área. Ele
   * leria isso como defeito do botão, não como o estado da conta. Dizer aqui o
   * que está acontecendo custa uma frase e economiza o chamado.
   */
  if (conta.status === 'disabled') {
    return {
      ok: false,
      status: 409,
      codigo: 'conta_desativada',
      motivo:
        'A conta desta pessoa está desativada — ela mesma não consegue entrar. Reative antes de olhar como ela.',
    };
  }

  return { ok: true };
}

/**
 * A sessão de suporte alcança ESTA área?
 *
 * ## O campo que era escrito e nunca lido
 *
 * `MarcaDeSuporte.areaId` guarda a área por onde o operador entrou — a mesma que
 * `podeEntrarComoAluno` autorizou e a mesma que a linha do `audit_log` nomeia. A
 * auditoria nº 5 mediu que ele era **escrito e nunca lido**: `grep` por
 * `suporte.areaId` devolvia a definição, a escrita, e zero leituras.
 *
 * A consequência não é quebra de inquilino — a autorização do lado do aluno
 * continua sendo `member_enrollment` casado por `memberId` + `agencyId`, e um
 * `admin` poderia abrir a outra área diretamente pelo painel. A consequência é
 * **quebra de registro**: uma sessão aberta "para a área X do cliente A" abria
 * toda área publicada em que aquele membro tivesse matrícula, em qualquer cliente
 * da agência, e o log continuava dizendo só "área X". Quem investigasse um
 * incidente leria uma frase verdadeira e incompleta, que é pior que nenhuma.
 *
 * ## Por que a marca ausente devolve `true`
 *
 * Porque a pergunta não é sobre a sessão normal. O aluno que entrou com a própria
 * senha alcança as áreas dele, e quem decide isso é `exigirMatricula`. Esta
 * função responde só o recorte extra que a impersonação impõe.
 *
 * ## Por que uma marca SEM `areaId` é recusada
 *
 * Uma sessão gravada antes deste campo existir de fato (ou por um caminho que
 * esquecesse de preenchê-lo) não tem como provar por onde entrou. O padrão
 * seguro é recusar: a sessão vive quinze minutos, e o operador refaz o clique.
 */
export function suporteAlcancaArea(
  marca: { areaId?: string | null } | undefined | null,
  areaId: string,
): boolean {
  if (!marca) return true;
  if (!marca.areaId) return false;
  return marca.areaId === areaId;
}

/**
 * A sessão de suporte já venceu?
 *
 * O Redis também expira a chave, e este relógio é a segunda tranca: o TTL do
 * Redis pode ser estendido por engano (a sessão do aluno **é** deslizante — ver
 * `renovarSessaoDoMembro`), e um instante gravado no payload não pode. Quem
 * esquecer de excluir a sessão de suporte da renovação encontra esta função no
 * caminho.
 */
export function suporteExpirou(iniciadaEm: number, agora = Date.now()): boolean {
  return agora - iniciadaEm >= SUPORTE_TTL_SEGUNDOS * 1000;
}

/** Quantos segundos faltam. Nunca negativo — a tela desenha uma barra com isto. */
export function segundosRestantesDeSuporte(iniciadaEm: number, agora = Date.now()): number {
  const restante = SUPORTE_TTL_SEGUNDOS * 1000 - (agora - iniciadaEm);
  return restante <= 0 ? 0 : Math.ceil(restante / 1000);
}

/**
 * O que a sessão de suporte deixa acontecer — e o que ela simula.
 *
 * ## Por que uma lista, e não um `if` em cada rota
 *
 * Porque a garantia que interessa é sobre **o que ainda não foi escrito**. Um
 * `if (suporte)` em cada escrita protege as sete rotas de hoje e não protege a
 * oitava, que alguém acrescenta em três meses sem saber que esta sessão existe.
 *
 * A regra é invertida: numa sessão de suporte, **todo método que muda estado é
 * recusado**, e só o que está aqui é liberado — porque foi conferido, uma vez,
 * que não escreve nada. Uma rota nova nasce recusada, e o sintoma disso é o
 * operador vendo "sessão de suporte não grava" em vez de o aluno vendo o
 * progresso dele adulterado.
 *
 * ## Por que estas três, e por que o certificado não está aqui
 *
 * O heartbeat, o botão de concluir e o envio da prova são **o produto sendo
 * testado**: metade da área de membros nunca foi exercida por ninguém porque
 * entrar exigia senha. As três respondem como responderiam de verdade — a prova
 * inclusive devolve a correção real — e nenhuma persiste.
 *
 * O certificado não. Simular a emissão devolveria um código que a página pública
 * de verificação não reconhece: uma mentira que só é descoberta quando alguém
 * abre o link. Recusar é a resposta honesta, e a frase diz por quê.
 */
const ACOES_SIMULADAS = new Set(['progresso', 'concluir', 'prova']);

export type AcaoDoAluno = string;

export function suporteSimula(acao: AcaoDoAluno | undefined): boolean {
  return acao !== undefined && ACOES_SIMULADAS.has(acao);
}
